import { Injectable } from '@angular/core';
import liff from '@line/liff';
import { environment } from 'src/environments/environment';

export interface ILineProfile {
  userId: string;
  displayName: string;
  pictureUrl?: string;
  statusMessage?: string;
}

export interface ILinePayload {
  profile: ILineProfile | null;
  idToken: string | null;
}

export const LINE_LINK_PENDING_KEY = 'yarrow.lineLink.pending';
export const LINE_LINK_RETURN_URL_KEY = 'yarrow.lineLink.returnUrl';

@Injectable({
  providedIn: 'root',
})
export class LineLiffService {
  private liffId = environment.LIFF_id;
  private initialized = false;

  /**
   * Initialize the LIFF SDK.
   * Must be called before any other LIFF method — typically in ngOnInit of the login page.
   * Idempotent: calling it more than once is safe.
   */
  async init(): Promise<void> {
    if (this.initialized) return;
    await liff.init({ liffId: this.liffId });
    this.initialized = true;
  }

  /**
   * Returns true when the user has already completed LINE login
   * (e.g. we just came back from the LINE redirect).
   */
  isLoggedIn(): boolean {
    return liff.isLoggedIn();
  }

  /** Returns whether the application is running inside the LINE client. */
  isInClient(): boolean {
    return liff.isInClient();
  }

  /** Returns whether the current LIFF context supports Share Target Picker. */
  isShareTargetPickerAvailable(): boolean {
    return liff.isApiAvailable('shareTargetPicker');
  }

  /** Share one ready-to-send LINE Message from the current LIFF context. */
  async shareMessage(message: any): Promise<boolean> {
    const result = await liff.shareTargetPicker([message]);
    return Boolean(result);
  }

  /** Close the current LIFF window without exposing the SDK to UI components. */
  closeWindow(): void {
    liff.closeWindow();
  }

  /**
   * Redirect the user to the LINE login page.
   * LINE will redirect back to the current URL after authentication.
   */
  login(redirectUri = `${environment.hostUrl}/login`): void {
    if (!liff.isLoggedIn()) {
      liff.login({ redirectUri });
    }
  }

  /**
   * Collect only the values required for the one-time backend exchange.
   */
  async getPayload(): Promise<ILinePayload> {
    const idToken = liff.getIDToken();
    let profile: ILineProfile | null = null;
    try {
      profile = await liff.getProfile();
    } catch {
      // Profile data is optional for authentication; the backend verifies idToken.
    }
    return { profile, idToken };
  }

  /**
   * Log the user out of LINE and clear the LIFF session.
   */
  logout(): void {
    liff.logout();
    this.initialized = false;
  }
}
